import { create } from 'zustand';
import { MarkupContext } from '@estimator/shared';

export interface OverrideValues {
  area?: number | null;
  length?: number | null;
  width?: number | null;
  height?: number | null;
  notes?: string | null;
}

export type ActiveTool = 'select' | 'area-rect' | 'linear' | 'polyline' | 'area-polygon' | 'angle' | 'count' | 'calibrate' | 'known-scale';

interface MarkupState {
  selectedMarkupId: string | null;
  hoveredMarkupId: string | null;
  pendingOverrides: Map<string, OverrideValues>;
  isDirty: boolean;
  currentPageId: string | null;
  currentDrawingId: string | null;
  currentRevisionId: string | null;

  // Tool & interaction state
  activeTool: ActiveTool;
  activeContext: MarkupContext;
  activeItemCategoryId: string | null;
  setActiveContext: (context: MarkupContext) => void;
  setActiveItemCategoryId: (id: string | null) => void;

  selectMarkup: (id: string | null) => void;
  hoverMarkup: (id: string | null) => void;
  setOverride: (markupId: string, field: string, value: number | string | null) => void;
  clearOverride: (markupId: string) => void;
  clearAllOverrides: () => void;
  setCurrentPage: (pageId: string) => void;
  setCurrentDrawing: (drawingId: string) => void;
  setCurrentRevision: (revisionId: string | null) => void;
  setActiveTool: (tool: ActiveTool) => void;
}

export const useMarkupStore = create<MarkupState>()((set) => ({
  selectedMarkupId: null,
  hoveredMarkupId: null,
  pendingOverrides: new Map(),
  isDirty: false,
  currentPageId: null,
  currentDrawingId: null,
  currentRevisionId: null,

  activeTool: 'select',
  activeContext: MarkupContext.FLOOR_PLAN,
  activeItemCategoryId: null,

  selectMarkup: (id) => set({ selectedMarkupId: id }),

  hoverMarkup: (id) => set({ hoveredMarkupId: id }),

  setOverride: (markupId, field, value) =>
    set((state) => {
      const next = new Map(state.pendingOverrides);
      const existing = next.get(markupId) ?? {};
      next.set(markupId, { ...existing, [field]: value });
      return { pendingOverrides: next, isDirty: true };
    }),

  clearOverride: (markupId) =>
    set((state) => {
      const next = new Map(state.pendingOverrides);
      next.delete(markupId);
      return { pendingOverrides: next, isDirty: next.size > 0 };
    }),

  clearAllOverrides: () => set({ pendingOverrides: new Map(), isDirty: false }),

  setCurrentPage: (pageId) => set({ currentPageId: pageId }),

  setCurrentDrawing: (drawingId) => set({ currentDrawingId: drawingId }),

  setCurrentRevision: (revisionId) => set({ currentRevisionId: revisionId }),

  setActiveTool: (tool) => set({ activeTool: tool }),

  setActiveContext: (context) => set({ activeContext: context }),
  setActiveItemCategoryId: (id) => set({ activeItemCategoryId: id }),
}));
